import { cn, getDayTimePeriod } from '@/lib/utils';
import { useAuth } from '@auth';
import { cva } from 'class-variance-authority';
import { useEffect, useMemo, useState } from 'react';
import SettingsDrawer from '@/components/SettingsDrawer';
import useOpenApiClient from '@/hooks/useOpenApiClient';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AccountTile } from '@/components/account-list/AccountTile';
import { AddReceiptEnrichButton } from '@/components/ui/AddReceiptButtonEnrich';
import HomePageE from '@/assets/icons/e_home_background.svg?react';
import { useLocation, useSearch } from 'wouter';
import AccountDetailsSheet from '@/components/account-list/AccountDetailsSheet';
import { GlowingStars } from '@/components/ui/GlowingStars';
import { Button } from '@/components/ui/button';
import { SnapAndEarn } from '@/components/account-list/SnapAndEarn';
import { HowItWorksSheet } from '@/components/account-list/HowItWorksSheet';
import { ConnectNewAccount } from '@/components/account-list/ConnectNewAccount';
import { components } from '@/types/api';

export default function AccountList() {
  const { user } = useAuth();
  const { apiFetch, userRole } = useOpenApiClient();
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();
  const searchString = useSearch();
  const [openHowItWorksSheet, setOpenHowItWorksSheet] = useState(false);
  const timePeriod = useMemo(getDayTimePeriod, []);

  const accountIdToEdit = useMemo(
    () => new URLSearchParams(searchString).get('account') || null,
    [searchString],
  );

  /**
   * another key __existingCacheCheck is used to differentiate between
   * this page results v/s if accounts are loaded somewhere else in the app
   * this page needs to track which accounts the user has already seen on the page
   * so that we can animate the new ones in
   */
  const accountsQuery = useQuery({
    queryKey: ['v1', 'accounts', '__existingCacheCheck'],
    queryFn: async () => {
      const existingAccountsInCache = queryClient.getQueryData<
        components['schemas']['AccountOutput'][]
      >(['v1', 'accounts', '__existingCacheCheck']);
      const res = await apiFetch.GET('/accounts');
      const accounts = res.data?.data;
      if (!accounts) {
        return accounts;
      }
      return accounts.map((account) => {
        const alreadyInCache = existingAccountsInCache?.some(
          (cachedAccount) => cachedAccount.id === account.id,
        );
        return { ...account, shouldAnimate: !alreadyInCache };
      });
    },
  });

  /**
   * mark all the accounts as not animating
   * after the first render, save in cache for subsequent renders
   */
  useEffect(() => {
    if (accountsQuery.data) {
      queryClient.setQueryData(
        ['v1', 'accounts', '__existingCacheCheck'],
        accountsQuery.data.map((account) => {
          return {
            ...account,
            shouldAnimate: false,
          };
        }),
      );
    }
  }, [accountsQuery.data, queryClient]);

  const accountsIsLoadedButEmpty = accountsQuery.isSuccess && accountsQuery.data?.length === 0;

  return (
    <div className={cn(pageVariants({ timePeriod }))}>
      <div className="z-10 mb-4 flex flex-auto flex-col bg-transparent">
        <div className="flex justify-end pt-2">
          <SettingsDrawer />
        </div>
        <h1
          className={cn(
            headingVariants({ timePeriod }),
            'animate-in fade-in slide-in-from-bottom-10 fill-mode-forwards duration-300 ease-out',
          )}
        >
          {user?.firstName ? `Good ${timePeriod}, ${user.firstName}` : 'Hello!'}
        </h1>
        {accountsIsLoadedButEmpty ? (
          <>
            <GlowingStars />
            <span className="text-foreground px-5 py-3 text-3xl leading-none font-bold">
              {userRole === 'live' ? (
                <>
                  Snap receipts &
                  <br />
                  earn gift cards!
                  <br />
                </>
              ) : (
                <>
                  Your enriching
                  <br />
                  experience is on
                  <br />
                  its way.
                </>
              )}
            </span>
            {userRole === 'live' ? (
              <div className="text-foreground w-full pr-21 pl-5 text-sm leading-normal font-normal">
                <p>
                  Help us grow simply scanning your receipts. The more unique receipts you scan, the
                  more you earn.
                </p>
              </div>
            ) : (
              <div className="text-foreground w-full px-9 py-14 text-center text-base leading-normal font-normal">
                <p>
                  “You will enrich your life immeasurable if you approach it with a sense of wonder
                  and discovery, and always challenge yourself to try new things.”
                </p>
                <p className="pt-2">—Nate Berkus</p>
              </div>
            )}
            {userRole === 'live' && (
              <>
                <div className="flex flex-col items-center justify-center gap-2">
                  <SnapAndEarn />
                  <Button
                    variant="ghost"
                    className={cn(howItWorksVariants({ timePeriod }))}
                    onClick={() => setOpenHowItWorksSheet(true)}
                  >
                    How it works
                  </Button>
                </div>
              </>
            )}
          </>
        ) : (
          <>
            <h3
              className={cn(
                subHeadingVariants({ timePeriod }),
                'animate-in fade-in slide-in-from-bottom-10 fill-mode-forwards duration-300 ease-out',
              )}
            >
              Welcome to the Enrich Network wallet
              <p className="text-foreground pt-2.5 text-sm leading-4 font-normal">
                {userRole === 'demo' && 'This demo includes sample accounts.'} Add receipts to see
                the magic
              </p>
            </h3>
            <section className="flex flex-auto flex-col items-end justify-end gap-1">
              {accountsQuery.data?.map((account, index) => (
                <AccountTile key={account.id} account={account} index={index} />
              ))}
              {userRole === 'demo' && accountsQuery.isSuccess && <ConnectNewAccount />}
            </section>
          </>
        )}

        <AddReceiptEnrichButton showReceiptsButton />
      </div>
      <div className="text- absolute inset-0 -z-0 h-full w-full overflow-clip">
        <HomePageE className="-z-0 h-auto w-full overflow-clip" />
      </div>
      <AccountDetailsSheet onClose={() => setLocation('/')} accountId={accountIdToEdit} />
      <HowItWorksSheet onClose={() => setOpenHowItWorksSheet(false)} open={openHowItWorksSheet} />
    </div>
  );
}

const pageVariants = cva('flex flex-col min-h-dvh -z-10 pb-24', {
  variants: {
    timePeriod: {
      morning: 'bg-white bg-[linear-gradient(0deg,#FFD8C4_0%,#FFD8C4_100%)]',
      afternoon: 'bg-[linear-gradient(154deg,#91DCD7_9.89%,#0CA5A2_96.98%)]',
      evening: 'bg-[linear-gradient(90deg,#0CA5A2_0%,#5CD7A3_100%)]',
    },
  },
});

const headingVariants = cva('text-[43px] font-extrabold leading-[43px] px-5', {
  variants: {
    timePeriod: {
      morning: 'text-primary',
      afternoon: 'text-primary-ocean-teal',
      evening: 'text-primary-light',
    },
  },
});

const subHeadingVariants = cva('text-3xl font-bold leading-9 px-5 pt-2.5 pb-7', {
  variants: {
    timePeriod: {
      morning: 'text-primary-foreground',
      afternoon: 'text-white',
      evening: 'text-white',
    },
  },
});

const howItWorksVariants = cva('text-md underline', {
  variants: {
    timePeriod: {
      morning: 'text-primary-foreground',
      afternoon: 'text-white',
      evening: 'text-white',
    },
  },
});
